import {Account, Plan, School, SchoolClass, User} from "models"
import React, {memo, useMemo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/src/shape-component"
import {digg} from "diggerize"
import envSense from "env-sense"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import {loading} from "components/layout/loading-blocker"
import Overlay from "components/layout/overlay"
import PropTypes from "prop-types"
import Step1 from "./step1"
import Step2 from "./step2"
import Step3 from "./step3"
import StepConfirm from "./step-confirm"
import StepCreditCard from "./step-credit-card"
import StepEanInvoiceInfo from "./step-ean-invoice-info"
import StepInvoiceInfo from "./step-invoice-info"
import StepSuccess from "./step-success"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import useI18n from "i18n-on-steroids/src/use-i18n.mjs"

export default memo(shapeComponent(class ComponentsPlansSignUpModal extends ShapeComponent {
  static defaultProps = {
    onBackPress: null,
    onRequestClose: null,
    onSignUpComplete: null,
    planResourceType: null,
    resource: null
  }

  static propTypes = {
    onBackPress: PropTypes.func,
    onRequestClose: PropTypes.func,
    onSignUpComplete: PropTypes.func,
    planResourceType: PropTypes.string,
    resource: PropTypes.oneOfType([PropTypes.instanceOf(School), PropTypes.instanceOf(SchoolClass), PropTypes.instanceOf(User)])
  }

  setup() {
    const {t} = useI18n({namespace: "js.components.plans.sign_up_modal.index"})
    const {planResourceType} = this.p

    useMemo(() => {
      if (planResourceType) {
        this.loadPlanFromResourceType()
      }
    }, [])

    this.setInstance({
      currentUser: useCurrentUser(),
      t
    })
    this.useStates({
      account: () => {
        if (this.p.resource instanceof SchoolClass) {
          return this.props.schoolClass?.school()?.account()
        }
      },
      invoiceInfo: undefined,
      paymentMethod: undefined,
      plan: undefined,
      period: undefined,
      price: undefined,
      resource: this.p.resource,
      step: planResourceType ? null : "step1"
    })
  }

  async loadPlanFromResourceType() {
    const plan = await Plan
      .ransack({identifier_eq: "pro"})
      .preload(["resources.periods.prices"])
      .select({
        Plan: ["description", "descriptionWithInlineStyling", "id", "identifier", "name", "subTitle"],
        PlanResource: ["id", "resourceName"],
        PlanResourcePeriod: ["id", "months", "name"],
        PlanResourcePeriodPrice: ["id", "price", "priceCents", "priceCurrency"]
      })
      .first()

    this.setState({
      plan,
      step: "step2"
    })
  }

  loadAccountIfNone() {
    if (!this.s.account) {
      this.loadAccount()
    }
  }

  async loadAccount() {
    let account = await Account
      .ransack({account_users_user_id_eq: this.tt.currentUser.id()})
      .first()

    if (!account) {
      const result = await this.tt.currentUser.createAccount()

      account = digg(result, "account")
    }

    this.setState({account})
  }

  render() {
    const {className, onRequestClose, planResourceType, ...restProps} = this.props
    const {account, paymentMethod, period, plan, price, resource, step} = this.s
    const {isNative} = envSense()

    return (
      <Overlay
        color={isNative ? "dark" : "blur"}
        dataSet={this.overlayDataSet ||= {component: "plans/sign-up-modal"}}
        onRequestClose={onRequestClose}
        zIndex={8}
        {...restProps}
      >
        {step == "step1" &&
          <Step1 onPlanChoosen={this.tt.onPlanChoosen} onSchoolClassChanged={this.tt.onSchoolClassChanged} resource={resource} />
        }
        {step == "step2" &&
          <Step2 onBackPressed={this.tt.onBackPressed} onPeriodChosen={this.tt.onPeriodChosen} plan={plan} resource={resource} />
        }
        {step == "step3" &&
          <Step3 onPaymentMethodChosen={this.tt.onPaymentMethodChosen} resource={resource} />
        }
        {step == "step-credit-card" && account &&
          <StepCreditCard account={account} onCreditCardAdded={this.tt.onCreditCardAdded} />
        }
        {step == "step-invoice-info" &&
          <StepInvoiceInfo onInvoiceInfoSubmit={this.tt.onInvoiceInfoSubmit} resource={resource} />
        }
        {step == "step-ean-invoice-info" &&
          <StepEanInvoiceInfo onEanInvoiceInfoSubmit={this.tt.onEanInvoiceInfoSubmit} />
        }
        {step == "step-confirm" &&
          <StepConfirm
            onBackPress={this.tt.onBackPressed}
            onConfirmUpgrade={this.tt.onConfirmUpgrade}
            paymentMethod={paymentMethod}
            period={period}
            price={price}
            resource={resource}
          />
        }
        {step == "step-success" &&
          <StepSuccess onRequestClose={this.tt.onStepSuccessRequestClose} />
        }
      </Overlay>
    )
  }

  onStepSuccessRequestClose = () => {
    if (this.p.onSignUpComplete) {
      this.p.onSignUpComplete()
    }

    if (this.p.onRequestClose) {
      this.p.onRequestClose()
    }
  }

  onBackPressed = () => {
    const {onBackPress} = this.p
    const {step} = this.s

    if (step == "step2") {
      if (onBackPress) {
        onBackPress()
      } else {
        this.setState({step: "step1"})
      }
    } else if (step == "step3") {
      this.setState({step: "step2"})
    } else if (step == "step-credit-card") {
      this.setState({step: "step3"})
    } else if (step == "step-invoice-info" || step == "step-ean-invoice-info") {
      this.setState({step: "step3"})
    } else if (step == "step-confirm") {
      this.setState({step: "step2"})
    } else {
      throw new Error(`Unknown step: ${step}`)
    }
  }

  onConfirmUpgrade = async () => {
    const {account, invoiceInfo} = this.s

    try {
      await loading(this.t(".subscribe_loading"), async () => {
        await this.s.plan.subscribeTo({
          account,
          invoice_info: {
            city: invoiceInfo?.city,
            ean_number: invoiceInfo?.eanNumber,
            email: invoiceInfo?.email,
            name: invoiceInfo?.name,
            note: invoiceInfo?.note,
            street_name: invoiceInfo?.streetName,
            zip_code: invoiceInfo?.zipCode,
            vat_number: invoiceInfo?.vatNumber
          },
          payment_method: this.s.paymentMethod,
          period: this.s.period,
          price: this.s.price,
          resource: this.s.resource
        })
        FlashMessage.success(this.t(".you_have_been_subscribed_to_pro"))
        this.setState({step: "step-success"})
      })
    } catch (error) {
      FlashMessage.errorResponse(error)
    }
  }

  onCreditCardAdded = () => {
    this.setState({step: "step-confirm"})
  }

  onEanInvoiceInfoSubmit = ({invoiceInfo}) => {
    this.loadAccountIfNone()
    this.setState({
      invoiceInfo,
      step: "step-confirm"
    })
  }

  onInvoiceInfoSubmit = ({invoiceInfo}) => {
    this.loadAccountIfNone()

    if (this.s.paymentMethod.identifier() == "credit_card") {
      this.setState({invoiceInfo, step: "step-credit-card"})
    } else {
      this.setState({invoiceInfo, step: "step-confirm"})
    }
  }

  onPeriodChosen = ({period, price}) => {
    if (this.p.resource instanceof User) {
      this.setState({period, price, step: "step-confirm"})
    } else {
      this.setState({period, price, step: "step3"})
    }
  }

  onPaymentMethodChosen = ({paymentMethod}) => {
    if (paymentMethod.identifier() == "ean_invoice") {
      this.setState({paymentMethod, step: "step-ean-invoice-info"})
    } else if (paymentMethod.identifier() == "invoice") {
      this.setState({paymentMethod, step: "step-invoice-info"})
    } else if (paymentMethod.identifier() == "credit_card") {
      this.setState({paymentMethod, step: "step-invoice-info"})
    } else {
      throw new Error(`Unknown payment method: ${paymentMethod.identifier()}`)
    }
  }

  onPlanChoosen = ({plan}) => {
    if (!this.s.resource) {
      FlashMessage.alert(this.t(".please_choose_a_class_to_upgrade"))

      return
    }

    this.setState({plan, step: "step2"})
  }

  onSchoolClassChanged = (schoolClass) => this.setState({account: schoolClass?.school()?.account(), resource: schoolClass})
}))
